import {
  bottleFormatSchema,
  defaultWineFilter,
  defaultWineOrder,
  filterWines,
  noWineFilter,
  orderWines,
  wineFilterIsDefault,
  wineOrderKeys,
  wineTypeSchema,
  type StockState,
  type WindowState,
  type WineFilterCriteria,
  type WineListRow,
  type WineOrder,
  type WineOrderKey,
} from "@corklog/shared";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router";

import { fetchWineList, markWinesSeen } from "../api.js";
import { useMayWrite } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { WineFilters } from "../components/WineFilters.js";
import { WineRow } from "../components/WineRow.js";
import { useReload } from "../offline.js";

/**
 * The wines in a cellar, and the way to find one of them.
 *
 * The list is the same row the drinking-window band lives in. A wine with the
 * four years draws its band; a wine without them says so rather than showing an
 * invented one, because a machine-made guess presented as a judgement is what
 * recording provenance exists to prevent.
 *
 * Registering happens on its own page. This one exists to answer *where is
 * that bottle* in front of a cabinet, and a ten-field form standing between
 * the heading and the first row is the wrong thing to meet on the way.
 *
 * The narrowing lives in the URL rather than in a `useState`, which is what
 * makes *filter the list, open a wine, come back* land on the filtered list
 * again instead of on all 158 rows. Typing replaces the history entry rather
 * than adding one, so Back leaves the list rather than retracing the search
 * one keystroke at a time.
 */
const CURRENT_YEAR = new Date().getFullYear();

/** The window states, listed here because the URL has to be checked against
 *  something: a hand-edited `?fonster=lagrad` is ignored rather than obeyed. */
const WINDOW_STATES: readonly WindowState[] = [
  "young",
  "ready",
  "peak",
  "past",
  "unknown",
];

/** The stock states as they are written in a link. */
const STOCK_PARAM: Readonly<Record<string, StockState>> = {
  alla: "any",
  slut: "outOfCellar",
  "kopa-igen": "buyAgain",
};

/**
 * The criteria as query parameters, in Swedish like the rest of the paths.
 * Anything unrecognised reads as *not narrowed by this* — a link that has been
 * truncated or edited should show too many wines, never none.
 *
 * `lager` is the exception, and has to be: its unnarrowed state is a value
 * rather than an absence, so a missing or garbled one falls back to the view
 * the list opens with instead of to *everything*. That is the one place a
 * broken link shows fewer wines rather than more, and it is the right way for
 * it to break — it lands on the screen's ordinary state, with the bar above
 * the list saying how many rows are being held back and offering them.
 */
function readCriteria(params: URLSearchParams): WineFilterCriteria {
  const type = wineTypeSchema.safeParse(params.get("typ"));
  const country = params.get("land");
  const window = params.get("fonster");
  const format = bottleFormatSchema.safeParse(params.get("format"));
  const stock = params.get("lager");
  return {
    query: params.get("sok") ?? "",
    type: type.success ? type.data : null,
    countryCode: country !== null && /^[A-Z]{2}$/u.test(country) ? country : null,
    window: WINDOW_STATES.find((state) => state === window) ?? null,
    format: format.success ? format.data : null,
    storage: params.get("skap") || null,
    // A shelf is only ever a shelf *in* a cabinet, so a link naming one alone
    // reads as no place at all rather than as a narrowing the bar cannot show.
    shelf: params.get("skap") ? params.get("hylla") || null : null,
    stock: (stock === null ? undefined : STOCK_PARAM[stock]) ?? defaultWineFilter.stock,
  };
}

/**
 * The orders as they are written in a link.
 *
 * Every key has a word now, the number included: until 2026-09-26 the number
 * was the list's own order and needed none, but the owner's list opens on
 * *Tillagd* and a guest's on the number, so the plain address means *the
 * reader's own default* and whichever of the two is not it has to be said.
 */
const ORDER_PARAM: Readonly<Record<string, WineOrderKey>> = {
  tillagd: "added",
  nummer: "number",
  fonster: "window",
  hojdpunkt: "peak",
  argang: "vintage",
  producent: "producer",
};

/**
 * The order out of the address. Read by the same forgiving rule as the
 * narrowing: an order that has been mangled, or one this reader is not offered
 * — *Tillagd* in a guest's hands — falls back to the reader's default rather
 * than to anything the link did not say.
 */
function readOrder(params: URLSearchParams, dated: boolean): WineOrder {
  const written = params.get("sortera");
  const key = written === null ? undefined : ORDER_PARAM[written];
  return {
    key:
      key !== undefined && wineOrderKeys(dated).includes(key)
        ? key
        : defaultWineOrder(dated).key,
    direction: params.get("riktning") === "fallande" ? "desc" : "asc",
  };
}

/**
 * The narrowing and the order, together, because they share one address and
 * whichever of them changes has to write the other back unchanged — otherwise
 * typing in the search field would quietly put the list back in number order.
 */
function writeParams(
  criteria: WineFilterCriteria,
  order: WineOrder,
  dated: boolean,
): URLSearchParams {
  const params = new URLSearchParams();
  if (criteria.query.trim() !== "") params.set("sok", criteria.query);
  if (criteria.type !== null) params.set("typ", criteria.type);
  if (criteria.countryCode !== null) params.set("land", criteria.countryCode);
  if (criteria.window !== null) params.set("fonster", criteria.window);
  if (criteria.format !== null) params.set("format", criteria.format);
  if (criteria.storage !== null) params.set("skap", criteria.storage);
  if (criteria.shelf !== null) params.set("hylla", criteria.shelf);
  // The default carries no parameter, so the plain address is the plain view.
  for (const [written, state] of Object.entries(STOCK_PARAM)) {
    if (criteria.stock === state) params.set("lager", written);
  }
  for (const [written, key] of Object.entries(ORDER_PARAM)) {
    if (order.key === key && key !== defaultWineOrder(dated).key) {
      params.set("sortera", written);
    }
  }
  if (order.direction === "desc") params.set("riktning", "fallande");
  return params;
}

/**
 * Where a cellar's narrowing is remembered between visits.
 *
 * Per cellar, because the narrowing is about what is in that cabinet: a
 * country or a type chosen for one cellar means nothing in another.
 */
function memoryKey(cellarId: string): string {
  return `corklog.wines.filter.${cellarId}`;
}

/**
 * The narrowing, kept across leaving the list and coming back.
 *
 * The address is still where it lives, and this is only what restores it when
 * there is no address to read it from — following *Viner* back from a wine, or
 * arriving from the cabinets, lands on the bare path and would otherwise land
 * on a list narrowed differently from the one just left.
 *
 * Stored as the query string rather than as JSON, so it goes back through the
 * same reader the address does. Nothing can drift between the two, and a
 * stored value that has been mangled is ignored exactly as a mangled link is.
 *
 * `sessionStorage`, not `localStorage`: remembered while this tab is open and
 * forgotten when the browser is closed. A filter that survived a week would
 * eventually be met as *where have my wines gone*, and the answer would be a
 * choice made on a different evening for a different reason. Every access is
 * guarded, because a browser set to refuse site data throws on the property
 * itself rather than returning nothing.
 */
function remember(cellarId: string, params: URLSearchParams): void {
  try {
    sessionStorage.setItem(memoryKey(cellarId), params.toString());
  } catch {
    // No memory, then. The list still works; it just opens unnarrowed.
  }
}

function remembered(cellarId: string): URLSearchParams | null {
  try {
    const stored = sessionStorage.getItem(memoryKey(cellarId));
    return stored === null || stored === "" ? null : new URLSearchParams(stored);
  } catch {
    return null;
  }
}

/**
 * Where in the list a wine was opened from, so coming back lands there.
 * Asked for by the owner on 2026-10-07.
 *
 * Written when a row is followed and taken — read and removed — the next time
 * the list is drawn, which is the return from that wine, by the browser's back
 * button or by *Viner* above the wine's heading alike. The browser cannot do
 * this itself: the list is drawn only once its rows have arrived, by which time
 * the browser has already restored the position onto a page with nothing on
 * it.
 *
 * Taken rather than kept, so it is spent once: a list reached later some other
 * way opens at the top, as a page newly arrived at does. In `sessionStorage`,
 * per cellar, for the reasons the narrowing is, and guarded the same way.
 */
function scrollKey(cellarId: string): string {
  return `corklog.wines.scroll.${cellarId}`;
}

function keepScroll(cellarId: string): void {
  try {
    sessionStorage.setItem(scrollKey(cellarId), String(Math.round(window.scrollY)));
  } catch {
    // Not kept, then; the list opens at the top.
  }
}

function takeScroll(cellarId: string): number | null {
  try {
    const stored = sessionStorage.getItem(scrollKey(cellarId));
    sessionStorage.removeItem(scrollKey(cellarId));
    const y = stored === null ? NaN : Number(stored);
    return Number.isFinite(y) && y > 0 ? y : null;
  } catch {
    return null;
  }
}

type State =
  | { status: "loading" }
  | { status: "ready"; wines: WineListRow[]; unreadNotes: number; newWines: number }
  | { status: "failed" };

export function Wines() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const [state, setState] = useState<State>({ status: "loading" });
  const [params, setParams] = useSearchParams();

  /**
   * Whether the rows say when each wine was added, which is what decides the
   * order the list opens in and whether *Tillagd* is offered at all.
   *
   * Read off the rows rather than off the role, so the server's answer is the
   * one that counts: the owner's rows carry the moment and nobody else's do,
   * and a browser that arrived before the API that sends it sees no moment and
   * stays in number order instead of sorting by nothing. False while the list
   * is loading, which costs nothing — there are no rows to put in order yet.
   */
  const dated =
    state.status === "ready" && state.wines.some((wine) => wine.addedAt !== null);

  const criteria = useMemo(() => readCriteria(params), [params]);
  const order = useMemo(() => readOrder(params, dated), [params, dated]);
  const setCriteria = useCallback(
    (next: WineFilterCriteria) => {
      setParams(writeParams(next, order, dated), {
        replace: true,
        preventScrollReset: true,
      });
    },
    [setParams, order, dated],
  );
  const setOrder = useCallback(
    (next: WineOrder) => {
      setParams(writeParams(criteria, next, dated), {
        replace: true,
        preventScrollReset: true,
      });
    },
    [setParams, criteria, dated],
  );

  /**
   * Whether the list is narrowed to what arrived since the last visit.
   *
   * **Local state rather than a filter criterion**, which is the one place
   * this departs from every other narrowing on this page. The criteria are
   * written into the address bar and remembered per tab, because they are a
   * question about the cellar that somebody may want to send to somebody else
   * — and *new to me* is not one. A link carrying it would open on a list of
   * nothing in the hands of the person it was sent to, which is a link that
   * appears broken.
   *
   * It also lives exactly as long as the marks do, for the same reason: both
   * are true of this visit.
   */
  const [onlyNew, setOnlyNew] = useState(false);

  const load = useCallback(() => {
    // The whole response rather than only its rows: the two indicators ride on
    // it, because this is the request this page already makes and they have to
    // be here — see `fetchWineList`.
    void fetchWineList(cellarId)
      .then((list) => {
        setState({
          status: "ready",
          wines: list.wines,
          unreadNotes: list.unreadNotes,
          newWines: list.newWines,
        });

        /**
         * The bookmark moves once the list is on screen, and never before.
         *
         * A GET that writes is a GET that lies, so this is its own request —
         * `10.10`'s rule, and it is what lets the marks stay for the visit
         * that earned them rather than clearing as they are drawn. Reloading
         * this page mid-visit does clear them, which is the honest reading of
         * *since you last looked* and the same property the notes indicator
         * has had since it was built.
         *
         * Nothing is said when it fails. The worst a lost bookmark does is
         * show the same news twice, and the cellar's front page is the last
         * screen that should carry an error about a timestamp nobody asked to
         * move.
         */
        if (list.newWines > 0) void markWinesSeen(cellarId).catch(() => {});
        // A reload that finds no news takes the narrowing with it. `useReload`
        // fires again when the tab comes back or the connection does, and the
        // second answer has the bookmark already moved — so a list left
        // narrowed would empty itself and take the button that undoes it away
        // in the same breath.
        if (list.newWines === 0) setOnlyNew(false);
      })
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  // Restored once, and only onto a bare address. A link somebody was given
  // carries its own narrowing and has to win over what this tab happens to
  // remember — otherwise a shared list would open on somebody else's filter.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    if (params.toString() !== "") return;
    const saved = remembered(cellarId);
    if (saved !== null) setParams(saved, { replace: true });
  }, [cellarId, params, setParams]);

  // Written on every change, including back to nothing: clearing the filter is
  // as much a decision as setting one, and a memory that only ever grew would
  // be impossible to get out of.
  useEffect(() => {
    remember(cellarId, params);
  }, [cellarId, params]);

  const wines = state.status === "ready" ? state.wines : [];
  // Narrowed first and ordered second, so the sort only ever handles the rows
  // that will be drawn. The filter keeps the order it is given, so the other
  // way round would give the same list and sort the hidden rows for nothing.
  const filtered = useMemo(
    () => orderWines(filterWines(wines, criteria, CURRENT_YEAR), order, CURRENT_YEAR),
    [wines, criteria, order],
  );
  // The news narrowing is applied over the ordinary one rather than inside it,
  // so the filter row goes on counting what it is narrowing and the two do not
  // have to agree about which of them is in charge.
  const visible = onlyNew ? filtered.filter((wine) => wine.isNew) : filtered;
  const mayWrite = useMayWrite();

  // Back to the list as it opens, on the owner's word of 2026-10-07: *Finns i
  // källaren* and nothing else. The news narrowing goes with it — it is not
  // drawn in the filter row, so somebody clearing the filter has no way of
  // seeing that it is one of the things narrowing the list, and a clear that
  // left something on would not do what it says.
  const reset = useCallback(() => {
    setOnlyNew(false);
    setCriteria(defaultWineFilter);
  }, [setCriteria]);

  // The place in the list, put back once it is drawn. Layout rather than an
  // ordinary effect so the list never paints at the top first and then jumps;
  // once per visit, because a reload while reading must not yank the page.
  const scrolled = useRef(false);
  useLayoutEffect(() => {
    if (scrolled.current || state.status !== "ready") return;
    scrolled.current = true;
    const y = takeScroll(cellarId);
    if (y !== null) window.scrollTo(0, y);
  }, [cellarId, state.status]);

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="wines" />

      {/* Registering a wine is a write, so a guest is not offered it — hidden
          rather than disabled, per `project-scope.md`. Nothing takes its place:
          the list below is what a guest came for, and it should start where the
          heading ends. */}
      {mayWrite === true && (
        <p className="mt-4">
          <Link
            to={`/kallare/${cellarId}/viner/nytt`}
            className="text-accent underline-offset-4 hover:underline"
          >
            {t("wines.add")}
          </Link>
        </p>
      )}

      {/* What somebody else has written here.

          It stands above the list because this is the page you land on when
          you open the cellar, and it is the only moment a note written by
          somebody else can reliably meet the person who would want to read it
          — the same argument the front page makes for carrying a transfer
          offer.

          **The line is always drawn, and only its emphasis comes and goes.**
          A link that appeared when there was news and vanished otherwise
          would take the old notes with it, and the cellar's memory of its own
          evenings would be reachable only in the week somebody happened to
          write one. That is the rule *Luckor* follows in the row of sections:
          the section stays and the page behind it says when there is nothing.

          It is also why this is not a tenth entry in that row, which refused
          one at `9.8` because eight already wrap onto three lines on a phone.
          The indicator is the way in, the way *Inventering* is reached from
          *Luckor*. */}
      {state.status === "ready" && (
        <p className="mt-4 max-w-prose text-sm">
          <Link
            to={`/kallare/${cellarId}/anteckningar`}
            className={`underline-offset-4 hover:underline ${
              state.unreadNotes > 0 ? "text-accent" : "text-muted"
            }`}
          >
            {state.unreadNotes > 0
              ? t("notes.areNew", { count: state.unreadNotes })
              : t("notes.title")}
          </Link>
        </p>
      )}

      {/* What has been added since this reader last looked.

          **Drawn only when there is news, which is where it parts company
          with the notes line above it.** That one is always drawn because it
          is the way in to a page, and a link that came and went would take
          the cellar's older evenings with it. This one leads nowhere: the new
          wines are in the list below already, so it narrows the page rather
          than leaving it — and a narrowing with nothing to narrow to is not a
          quiet line, it is a line about nothing.

          A button rather than a link for the same reason: nothing is
          navigated to, and the address bar must not carry *new to me* —
          a link somebody was sent would open on an empty list in their hands.

          The owner never sees it. They are the only person who registers a
          wine here, so the server sends them nought; nothing on this screen
          has to know that. */}
      {state.status === "ready" && state.newWines > 0 && (
        <p className="mt-2 max-w-prose text-sm">
          <button
            type="button"
            onClick={() => setOnlyNew((shown) => !shown)}
            className="text-accent underline-offset-4 hover:underline"
          >
            {onlyNew
              ? t("wines.showAllAgain")
              : t("wines.newSinceLast", { count: state.newWines })}
          </button>
        </p>
      )}

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("wines.loadFailed")}
        </p>
      )}

      {state.status === "ready" &&
        (state.wines.length === 0 ? (
          <p className="text-muted mt-8 max-w-prose text-sm">
            {/* An empty cellar tells a guest that it is empty and stops there.
             *Registrera ett vin* is the owner's next step, not theirs. */}
            {mayWrite === true ? (
              <>
                {t("wines.empty")}{" "}
                <Link
                  to={`/kallare/${cellarId}/viner/nytt`}
                  className="text-accent underline-offset-4 hover:underline"
                >
                  {t("wines.add")}
                </Link>
              </>
            ) : (
              t("wines.emptyForGuest")
            )}
          </p>
        ) : (
          <>
            <WineFilters
              wines={state.wines}
              criteria={criteria}
              onChange={setCriteria}
              onReset={reset}
              shown={visible.length}
              order={order}
              onOrderChange={setOrder}
              dated={dated}
            />

            {visible.length === 0 ? (
              <p className="text-muted mt-8 max-w-prose text-sm">
                {t("wines.filter.noMatch")}{" "}
                {/* Clearing goes back to the list's own narrowing. When that is
                    already what is chosen and it is still empty, every wine in
                    the cellar is finished, and the only way out is to show
                    them — so that is what is offered, under its own name. */}
                {wineFilterIsDefault(criteria) && !onlyNew ? (
                  <button
                    type="button"
                    onClick={() => setCriteria(noWineFilter)}
                    className="text-accent underline-offset-4 hover:underline"
                  >
                    {t("wines.filter.clear")}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={reset}
                    className="text-accent underline-offset-4 hover:underline"
                  >
                    {t("wines.filter.reset")}
                  </button>
                )}
              </p>
            ) : (
              <ul className="border-line mt-8 border-t">
                {visible.map((wine) => (
                  <Link
                    key={wine.id}
                    to={`/kallare/${cellarId}/viner/${wine.id}`}
                    onClick={() => keepScroll(cellarId)}
                    className="block"
                    // The row is an <article>, and an article inside a link
                    // stops the link taking its name from what it contains —
                    // so without this a screen reader announces "link" and
                    // nothing else, once per wine, down the whole cellar.
                    // Found by the browser journeys in 7.6, which could not
                    // find the row by name either.
                    aria-label={wine.name}
                  >
                    <WineRow
                      currentYear={CURRENT_YEAR}
                      wine={{
                        number: wine.number,
                        name: wine.name,
                        producer: wine.producer,
                        region: wine.region,
                        countryCode: wine.countryCode,
                        vintage: wine.isNonVintage ? null : wine.vintage,
                        window: {
                          drinkFrom: wine.drinkFrom,
                          peakFrom: wine.peakFrom,
                          peakTo: wine.peakTo,
                          drinkBy: wine.drinkBy,
                        },
                        standing: wine.standing,
                        away: wine.away,
                        ratings: wine.ratings,
                        isNew: wine.isNew,
                      }}
                      // `10.21`: on the list of what to buy again, the number
                      // the shop answers to, where one was written down. Only
                      // there — on every other list it would be a column of
                      // figures nobody is about to use.
                      {...(criteria.stock === "buyAgain" && wine.systembolagetId !== null
                        ? {
                            note: t("wines.articleNumber", {
                              number: wine.systembolagetId,
                            }),
                          }
                        : {})}
                    />
                  </Link>
                ))}
              </ul>
            )}
          </>
        ))}
    </section>
  );
}
